import { useState, useEffect, useContext } from "react";
import { CancelIcon, ICON_SIZE, PasskeyIcon } from "./actionIcons";
import ReactDOM from "react-dom";
import { GlobalContext } from "./GlobalContext";
import { apiFetch } from "./api.js";
import {
  browserSupportsWebAuthn,
  platformAuthenticatorIsAvailable,
  startRegistration,
} from "@simplewebauthn/browser";
import { Fingerprint } from "lucide-react";
import { describeDevice } from "./deviceLabel";
import { emitter } from "./UtilityFunctions";

//Whether this prompt is currently on screen. WelcomeTourPrompt is eligible in
//exactly the same moment as this one — the first login after the forced
//password change — and two stacked modals is the same broken screen this
//component already refuses to be half of. So it queues behind us: it checks
//this before showing, and the "prompt:closed" event below is its cue to retry.
let promptOpen = false;
export const isPasskeyPromptOpen = () => promptOpen;

/**
 * One-time offer to set up biometric sign-in, shown after login on a device
 * that can actually do it.
 *
 * This exists because discovery is the whole problem: a passkey buried in a
 * profile menu gets set up by nobody, and a feature nobody enables saves nobody
 * any typing. It is deliberately dismissible and asked once per user per
 * device — a prompt that keeps coming back is worse than no prompt.
 */
function PasskeyPrompt() {
  const globals = useContext(GlobalContext);
  const userID = globals.userData?.UserID;
  //User-scoped, following the repo's draft-key convention: on a shared counter
  //machine one person's "not now" must not silence the offer for the next.
  const dismissKey = userID ? `passkeyPromptDismissed_u${userID}` : null;

  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    //Never stack this on the forced first-login password change: that modal
    //cannot be dismissed, so two at once is simply a broken screen. The
    //password change ends the session anyway, so the offer arrives on the very
    //next login instead — arguably a better moment for it.
    if (!userID || globals.userData?.FirstLogin) return;
    if (!dismissKey || localStorage.getItem(dismissKey) === "true") return;
    if (!browserSupportsWebAuthn()) return;

    (async () => {
      try {
        //Only offer where there is a fingerprint reader / Face ID / Hello. On a
        //desktop with neither, "use your fingerprint" is a confusing thing to
        //be asked out of nowhere.
        if (!(await platformAuthenticatorIsAvailable())) return;

        const res = await apiFetch("/passkeys/list", { body: {} });
        const obj = await res.json();
        //Nothing to offer if the server has no passkey config, or if this user
        //already has one — they have already made the choice.
        if (!obj?.ok || !obj.enabled || (obj.passkeys || []).length > 0) return;

        if (!cancelled) { promptOpen = true; setVisible(true); }
      } catch {
        //A failed probe means no prompt. Never an error the user has to read.
      }
    })();

    return () => { cancelled = true; };
  }, [userID, globals.userData?.FirstLogin, dismissKey]);

  const dismiss = () => {
    if (dismissKey) localStorage.setItem(dismissKey, "true");
    promptOpen = false;
    setVisible(false);
    emitter.emit("prompt:closed", "passkey");
  };

  async function handleSetUp() {
    setError("");
    setBusy(true);
    try {
      const optRes = await apiFetch("/passkeys/register/options", { body: {} });
      const optObj = await optRes.json();
      if (!optRes.ok || !optObj.ok) { setError("Could not start setup."); return; }

      const attestation = await startRegistration({ optionsJSON: optObj.options });

      //Label it, exactly as the Security & passkeys modal does. Without this a
      //passkey enrolled from the prompt lands with DeviceLabel NULL and shows
      //as a bare "Passkey" in the device list — on the one screen whose whole
      //job is telling your devices apart so you can remove the right one.
      //Most passkeys will be created HERE, not in the modal, so this was the
      //path that mattered.
      const verRes = await apiFetch("/passkeys/register/verify", {
        body: { response: attestation, label: describeDevice(navigator.userAgent) },
      });
      const verObj = await verRes.json();
      if (!verRes.ok || !verObj.ok) { setError("Could not finish setup."); return; }

      //Success also counts as answered — never ask again on this device.
      dismiss();
    } catch (err) {
      //Cancel and timeout are indistinguishable by design. Either way the
      //answer was "not now", so close quietly rather than nagging.
      if (err?.name === "NotAllowedError" || err?.name === "AbortError") { dismiss(); return; }
      setError("Could not set this device up. You can try later from your profile menu.");
    } finally {
      setBusy(false);
    }
  }

  if (!visible) return null;

  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 50,
      }}
      onClick={(e) => { if (e.target === e.currentTarget && !busy) dismiss(); }}
    >
      <div
        style={{
          background: 'var(--theme-surface)',
          borderRadius: 'var(--theme-radius-lg)',
          width: '400px', maxWidth: '90vw',
          boxShadow: 'var(--theme-shadow-dialog)',
        }}
      >
        <div className="modal-body-padded" style={{ textAlign: 'center' }}>
          <Fingerprint
            className="size-10"
            style={{ margin: '0 auto 12px', color: 'var(--theme-accent)' }}
            aria-hidden="true"
          />
          <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>
            Sign in faster next time
          </h3>
          <p className="hint-t">
            Use your fingerprint or face to sign in on this device instead of typing your
            password. Your password will still work.
          </p>
          {error && <p className="error-t" style={{ marginTop: '8px' }}>{error}</p>}
        </div>
        <div className="modal-shell-footer">
          <button type="button" className="btn-t btn-t-secondary" onClick={dismiss} disabled={busy}><CancelIcon size={ICON_SIZE} />
            Not now
          </button>
          <button type="button" className="btn-t btn-t-primary" onClick={handleSetUp} disabled={busy}><PasskeyIcon size={ICON_SIZE} />
            {busy ? 'Waiting for your device…' : 'Set up'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default PasskeyPrompt;
